import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useHistory, COMMAND_TYPES } from './HistoryContext';

const ContainerContext = createContext();

export const useContainers = () => {
  const context = useContext(ContainerContext);
  if (!context) {
    throw new Error('useContainers must be used within a ContainerProvider');
  }
  return context;
};

// Helper function to generate unique IDs
const generateId = (prefix = 'container') => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `${prefix}-${timestamp}-${random}`;
};

// Helper function to calculate aspect ratio
// const calculateAspectRatio = (width, height) => {
//   return width / height;
// }; // Commented: unused function

export const ContainerProvider = ({ children }) => {
  const [containers, setContainers] = useState([]);
  const [selectedContainerId, setSelectedContainerId] = useState(null);
  const [activeContainerForDrawing, setActiveContainerForDrawing] = useState(null);
  const [hoveredContainerId, setHoveredContainerId] = useState(null);
  const [hoverMenuPosition, setHoverMenuPosition] = useState({ x: 0, y: 0 });
  
  const { pushCommand } = useHistory();
  const hoverTimeoutRef = useRef(null);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);
  
  // Helper to create lightweight state for history (without large image data)
  const createLightweightState = useCallback((container) => {
    const lightweight = { ...container };
    
    // Remove large image data for history, keep only essential properties
    if (lightweight.image && lightweight.image.src) {
      lightweight.image = {
        ...lightweight.image,
        src: `[IMAGE_DATA_${lightweight.image.src.length}_BYTES]`, // Placeholder
        width: lightweight.image.width,
        height: lightweight.image.height,
        originalAspectRatio: lightweight.image.originalAspectRatio
      };
    }
    
    // Also compress drawings if they're large
    if (lightweight.drawings && lightweight.drawings.length > 10) {
      lightweight.drawings = lightweight.drawings.slice(-5); // Keep only last 5 drawings
    }
    
    return lightweight;
  }, []);
  
  // Add a new container
  const addContainer = useCallback((containerData, skipHistory = false) => {
    // Calculate zIndex for new container (highest existing + 1)
    const maxZIndex = containers.length > 0 ? Math.max(...containers.map(c => c.zIndex || 0)) : -1;
    const newZIndex = containerData.zIndex !== undefined ? containerData.zIndex : maxZIndex + 1;
    
    const newContainer = {
      id: containerData.id || generateId(),
      type: containerData.type || 'image',
      x: containerData.x || 0,
      y: containerData.y || 0,
      width: containerData.width || 100,
      height: containerData.height || 100,
      rotation: containerData.rotation || 0,
      scaleX: containerData.scaleX || 1,
      scaleY: containerData.scaleY || 1,
      opacity: containerData.opacity !== undefined ? containerData.opacity : 1,
      isLocked: containerData.isLocked || false,
      layerId: containerData.layerId || 'default-layer',
      name: containerData.name || 'Unnamed Container',
      imageSrc: containerData.imageSrc || null,
      drawings: containerData.drawings || [],
      zIndex: newZIndex, // Ensure zIndex is always set
      createdAt: containerData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...containerData
    };

    setContainers(prevContainers => {
      const newContainers = [...prevContainers, newContainer];
      return newContainers;
    });

    if (!skipHistory) {
      try {
      pushCommand({
        type: COMMAND_TYPES.ADD_CONTAINER,
        targetId: newContainer.id,
        newState: newContainer, // Keep full state for ADD operations to enable proper undo/redo
        containerCount: containers.length + 1
      });
      } catch (error) {
        console.warn('[ContainerContext] Failed to add container to history:', error);
        // If history fails due to storage issues, still allow the container to be created
        // but inform the user that undo/redo may not work for this action
        if (error.message && error.message.includes('quota')) {
          console.warn('localStorage quota exceeded while adding container to history');
          if (window.dndMapCleanupHistory) {
            setTimeout(() => {
              window.dndMapCleanupHistory();
            }, 100);
          }
        }
      }
    }

    // Auto-select the new container
    setSelectedContainerId(newContainer.id);
    
    return newContainer;
  }, [containers, pushCommand]);

  // Update an existing container
  const updateContainer = useCallback((containerId, updates, skipHistory = false) => {
    if (!containerId || !updates) return;

    // Skip history for certain updates like lock/zIndex to avoid cluttering history
    if (skipHistory || (Object.keys(updates).length === 1 && ('isLocked' in updates || 'zIndex' in updates))) {
      // Direct update without history
      setContainers(prevContainers => 
        prevContainers.map(container => 
          container.id === containerId 
            ? { ...container, ...updates }
            : container
        )
      );
      return;
    }

    let oldContainer = null;
    let updatedContainer = null;
    
    setContainers(prev => {
      const containerIndex = prev.findIndex(c => c.id === containerId);
      if (containerIndex === -1) return prev;
      
      oldContainer = prev[containerIndex];
      updatedContainer = {
        ...oldContainer,
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      const updated = [...prev];
      updated[containerIndex] = updatedContainer;
      
      return updated;
    });
    
    // Push to history after state update (outside of setState)
    if (oldContainer && updatedContainer) {
      setTimeout(() => {
        // Determine if this is a simple transform update (position, scale, rotation)
        const isTransformUpdate = Object.keys(updates).every(key => 
          ['x', 'y', 'scaleX', 'scaleY', 'rotation'].includes(key)
        );
        
        // Skip history for lock updates (handled by toggleLock)
        const isLockUpdate = Object.keys(updates).includes('locked');
        
        // Skip history for zIndex updates (handled by bringToFront/sendToBack)
        const isZIndexUpdate = Object.keys(updates).includes('zIndex');
        
        if (isLockUpdate || isZIndexUpdate) {
          return;
        }
        
        // For transform updates, use lightweight history to save memory
        if (isTransformUpdate) {
          pushCommand({
            type: COMMAND_TYPES.UPDATE_CONTAINER,
            targetId: containerId,
            previousState: createLightweightState(oldContainer),
            newState: createLightweightState(updatedContainer),
            updateType: Object.keys(updates).join(','),
            isLightweight: true
          });
        } else {
          // For other updates (like adding drawings), use full state but still lightweight
          pushCommand({
            type: COMMAND_TYPES.UPDATE_CONTAINER,
            targetId: containerId,
            previousState: createLightweightState(oldContainer),
            newState: createLightweightState(updatedContainer),
            updateType: Object.keys(updates).join(',')
          });
        }
      }, 0);
    }
  }, [pushCommand, createLightweightState]);
  
  // Delete a container
  const deleteContainer = useCallback((id) => {
    let containerToDelete = null;
    let newLength = 0;
    
    setContainers(prev => {
      containerToDelete = prev.find(c => c.id === id);
      if (!containerToDelete) return prev;
      
      const updated = prev.filter(c => c.id !== id);
      newLength = updated.length;
      
      return updated;
    });
    
    // Push to history after state update (outside of setState)
    if (containerToDelete) {
      setTimeout(() => {
        pushCommand({
          type: COMMAND_TYPES.DELETE_CONTAINER,
          targetId: id,
          previousState: containerToDelete, // Keep full state for DELETE operations to enable proper undo/redo
          containerCount: newLength
        });
      }, 0);
    }
    
    // Clear selection if deleted container was selected
    if (selectedContainerId === id) {
      setSelectedContainerId(null);
    }
    if (activeContainerForDrawing === id) {
      setActiveContainerForDrawing(null);
    }
  }, [selectedContainerId, activeContainerForDrawing, pushCommand]);
  
  // Transform container (optimized for frequent updates with debounced history)
  const transformTimeoutRef = useRef(null);
  const lastTransformStateRef = useRef({});
  
  // Generic transform container function with debounced history
  const transformContainer = useCallback((id, transforms) => {
    setContainers(prev => {
      const containerIndex = prev.findIndex(c => c.id === id);
      if (containerIndex === -1) return prev;
      
      const oldContainer = prev[containerIndex];
      const updated = [...prev];
      updated[containerIndex] = {
        ...updated[containerIndex],
        ...transforms,
        updatedAt: new Date().toISOString()
      };
      
      // Store initial state for history if this is the first transform
      if (!lastTransformStateRef.current[id]) {
        lastTransformStateRef.current[id] = createLightweightState(oldContainer);
      }
      
      // Clear existing timeout
      if (transformTimeoutRef.current) {
        clearTimeout(transformTimeoutRef.current);
      }
      
      // Set new timeout to save to history after transformation stops
      transformTimeoutRef.current = setTimeout(() => {
        const initialState = lastTransformStateRef.current[id];
        if (initialState) {
          pushCommand({
            type: COMMAND_TYPES.UPDATE_CONTAINER,
            targetId: id,
            previousState: initialState,
            newState: createLightweightState(updated[containerIndex]),
            updateType: Object.keys(transforms).join(','),
            isLightweight: true,
            isTransformOperation: true
          });
          
          // Clear the stored state
          delete lastTransformStateRef.current[id];
        }
      }, 300); // Save to history 300ms after transformation stops
      
      return updated;
    });
  }, [pushCommand, createLightweightState]);

  // Move container (wrapper for transformContainer)
  const moveContainer = useCallback((id, x, y) => {
    transformContainer(id, { x, y });
  }, [transformContainer]);

  // Scale container (wrapper for transformContainer)
  const scaleContainer = useCallback((id, scaleX, scaleY) => {
    transformContainer(id, { scaleX, scaleY });
  }, [transformContainer]);

  // Rotate container (wrapper for transformContainer)
  const rotateContainer = useCallback((id, rotation) => {
    transformContainer(id, { rotation });
  }, [transformContainer]);
  
  // Add drawing to container
  const addDrawingToContainer = useCallback((containerId, drawing, skipHistory = false) => {
    const newDrawing = {
      id: generateId('drawing'),
      ...drawing,
      relativeToContainer: true,
      createdAt: new Date().toISOString()
    };
    
    let drawingCount = 0;
    
    setContainers(prev => {
      const containerIndex = prev.findIndex(c => c.id === containerId);
      if (containerIndex === -1) return prev;
      
      const oldContainer = prev[containerIndex];
      
      // Ensure drawings exists and is an array before spreading
      const existingDrawings = oldContainer.drawings && Array.isArray(oldContainer.drawings) ? oldContainer.drawings : [];
      
      const updatedContainer = {
        ...oldContainer,
        drawings: [...existingDrawings, newDrawing],
        updatedAt: new Date().toISOString()
      };
      
      const updated = [...prev];
      updated[containerIndex] = updatedContainer;
      drawingCount = updatedContainer.drawings.length;
      
      return updated;
    });
    
    // Push to history after state update (outside of setState) - only if not skipping
    if (!skipHistory) {
    setTimeout(() => {
      pushCommand({
        type: COMMAND_TYPES.ADD_DRAWING_TO_CONTAINER,
        targetId: containerId,
        drawingId: newDrawing.id,
        newDrawing: newDrawing,
        drawingCount: drawingCount
      });
    }, 0);
    }
    
    return newDrawing;
  }, [pushCommand]);
  
  // Update drawing in container
  const updateDrawingInContainer = useCallback((containerId, drawingId, updates) => {
    let oldDrawing = null;
    let updatedDrawing = null;
    
    setContainers(prev => {
      const containerIndex = prev.findIndex(c => c.id === containerId);
      if (containerIndex === -1) return prev;
      
      const container = prev[containerIndex];
      
      // Ensure drawings exists and is an array
      if (!container.drawings || !Array.isArray(container.drawings)) {
        return prev;
      }
      
      const drawingIndex = container.drawings.findIndex(d => d.id === drawingId);
      if (drawingIndex === -1) return prev;
      
      oldDrawing = container.drawings[drawingIndex];
      updatedDrawing = { ...oldDrawing, ...updates };
      
      const updatedDrawings = [...container.drawings];
      updatedDrawings[drawingIndex] = updatedDrawing;
      
      const updatedContainer = {
        ...container,
        drawings: updatedDrawings,
        updatedAt: new Date().toISOString()
      };
      
      const updated = [...prev];
      updated[containerIndex] = updatedContainer;
      
      return updated;
    });
    
    // Push to history after state update (outside of setState)
    if (oldDrawing && updatedDrawing) {
      setTimeout(() => {
        pushCommand({
          type: COMMAND_TYPES.UPDATE_DRAWING_IN_CONTAINER,
          targetId: containerId,
          drawingId: drawingId,
          previousDrawing: oldDrawing,
          newDrawing: updatedDrawing,
          updateType: Object.keys(updates).join(',')
        });
      }, 0);
    }
  }, [pushCommand]);
  
  // Delete drawing from container
  const deleteDrawingFromContainer = useCallback((containerId, drawingId) => {
    let drawingToDelete = null;
    let drawingCount = 0;
    
    setContainers(prev => {
      const containerIndex = prev.findIndex(c => c.id === containerId);
      if (containerIndex === -1) return prev;
      
      const container = prev[containerIndex];
      
      // Ensure drawings exists and is an array
      if (!container.drawings || !Array.isArray(container.drawings)) {
        return prev;
      }
      
      drawingToDelete = container.drawings.find(d => d.id === drawingId);
      if (!drawingToDelete) return prev;
      
      const updatedContainer = {
        ...container,
        drawings: container.drawings.filter(d => d.id !== drawingId),
        updatedAt: new Date().toISOString()
      };
      
      const updated = [...prev];
      updated[containerIndex] = updatedContainer;
      drawingCount = updatedContainer.drawings.length;
      
      return updated;
    });
    
    // Push to history after state update (outside of setState)
    if (drawingToDelete) {
      setTimeout(() => {
        pushCommand({
          type: COMMAND_TYPES.DELETE_DRAWING_FROM_CONTAINER,
          targetId: containerId,
          drawingId: drawingId,
          deletedDrawing: drawingToDelete,
          drawingCount: drawingCount
        });
      }, 0);
    }
  }, [pushCommand]);
  
  // Get containers by layer
  const getContainersByLayer = useCallback((layerId) => {
    return containers.filter(container => container.layerId === layerId);
  }, [containers]);
  
  // Get selected container
  const getSelectedContainer = useCallback(() => {
    return containers.find(c => c.id === selectedContainerId) || null;
  }, [containers, selectedContainerId]);
  
  // Toggle container lock with history
  const toggleLock = useCallback((id) => {
    const container = containers.find(c => c.id === id);
    if (!container) return;
    
    const oldLocked = container.locked;
    const newLocked = !oldLocked;
    
    // Update container state directly (without triggering updateContainer's history)
    setContainers(prev => {
      const containerIndex = prev.findIndex(c => c.id === id);
      if (containerIndex === -1) return prev;
      
      const updated = [...prev];
      updated[containerIndex] = {
        ...updated[containerIndex],
        locked: newLocked,
        updatedAt: new Date().toISOString()
      };
      
      return updated;
    });
    
    // Push specific command to history
    setTimeout(() => {
      pushCommand({
        type: COMMAND_TYPES.TOGGLE_LOCK_CONTAINER,
        targetId: id,
        previousLocked: oldLocked,
        newLocked: newLocked,
        containerName: container.name
      });
    }, 0);
  }, [containers, pushCommand]);

  // Move container one step forward in z-index
  const bringToFront = useCallback((id) => {
    const container = containers.find(c => c.id === id);
    if (!container) return;
    
    // Find the next higher z-index
    const higherContainers = containers.filter(c => c.zIndex > container.zIndex);
    if (higherContainers.length === 0) {
      // Already at the top, no change needed
      return;
    }
    
    // Find the lowest z-index that's higher than current
    const nextZIndex = Math.min(...higherContainers.map(c => c.zIndex));
    
    // Store old state for history
    const oldZIndex = container.zIndex;
    const containerToMoveDown = containers.find(c => c.zIndex === nextZIndex);
    const otherOldZIndex = containerToMoveDown ? containerToMoveDown.zIndex : null;
    
    // Update z-indices directly (without triggering updateContainer's history)
    setContainers(prev => {
      const updated = [...prev];
      
      // Update main container
      const mainIndex = updated.findIndex(c => c.id === id);
      if (mainIndex !== -1) {
        updated[mainIndex] = {
          ...updated[mainIndex],
          zIndex: nextZIndex,
          updatedAt: new Date().toISOString()
        };
      }
      
      // Update affected container
      if (containerToMoveDown) {
        const affectedIndex = updated.findIndex(c => c.id === containerToMoveDown.id);
        if (affectedIndex !== -1) {
          updated[affectedIndex] = {
            ...updated[affectedIndex],
            zIndex: oldZIndex,
            updatedAt: new Date().toISOString()
          };
        }
      }
      
      return updated;
    });
    
    // Push to history
    setTimeout(() => {
      pushCommand({
        type: COMMAND_TYPES.CHANGE_CONTAINER_Z_INDEX,
        targetId: id,
        action: 'bringToFront',
        previousZIndex: oldZIndex,
        newZIndex: nextZIndex,
        affectedContainerId: containerToMoveDown?.id,
        affectedPreviousZIndex: otherOldZIndex,
        affectedNewZIndex: oldZIndex,
        containerName: container.name
      });
    }, 0);
  }, [containers, pushCommand]);
  
  // Move container one step backward in z-index
  const sendToBack = useCallback((id) => {
    const container = containers.find(c => c.id === id);
    if (!container) return;
    
    // Find the next lower z-index
    const lowerContainers = containers.filter(c => c.zIndex < container.zIndex);
    if (lowerContainers.length === 0) {
      // Already at the bottom, no change needed
      return;
    }
    
    // Find the highest z-index that's lower than current
    const nextZIndex = Math.max(...lowerContainers.map(c => c.zIndex));
    
    // Store old state for history
    const oldZIndex = container.zIndex;
    const containerToMoveUp = containers.find(c => c.zIndex === nextZIndex);
    const otherOldZIndex = containerToMoveUp ? containerToMoveUp.zIndex : null;
    
    // Update z-indices directly (without triggering updateContainer's history)
    setContainers(prev => {
      const updated = [...prev];
      
      // Update main container
      const mainIndex = updated.findIndex(c => c.id === id);
      if (mainIndex !== -1) {
        updated[mainIndex] = {
          ...updated[mainIndex],
          zIndex: nextZIndex,
          updatedAt: new Date().toISOString()
        };
      }
      
      // Update affected container
      if (containerToMoveUp) {
        const affectedIndex = updated.findIndex(c => c.id === containerToMoveUp.id);
        if (affectedIndex !== -1) {
          updated[affectedIndex] = {
            ...updated[affectedIndex],
            zIndex: oldZIndex,
            updatedAt: new Date().toISOString()
          };
        }
      }
      
      return updated;
    });
    
    // Push to history
    setTimeout(() => {
      pushCommand({
        type: COMMAND_TYPES.CHANGE_CONTAINER_Z_INDEX,
        targetId: id,
        action: 'sendToBack',
        previousZIndex: oldZIndex,
        newZIndex: nextZIndex,
        affectedContainerId: containerToMoveUp?.id,
        affectedPreviousZIndex: otherOldZIndex,
        affectedNewZIndex: oldZIndex,
        containerName: container.name
      });
    }, 0);
  }, [containers, pushCommand]);
  
  // Duplicate container with history
  const duplicateContainer = useCallback((id, offset = { x: 50, y: 50 }) => {
    const container = containers.find(c => c.id === id);
    if (!container) return null;
    
    // Calculate new zIndex (higher than all existing)
    const maxZIndex = containers.length > 0 ? Math.max(...containers.map(c => c.zIndex || 0)) : -1;
    
    const duplicatedContainer = {
      ...container,
      id: generateId('container'),
      name: `${container.name} (Copy)`,
      x: container.x + offset.x,
      y: container.y + offset.y,
      zIndex: maxZIndex + 1, // Ensure duplicated container appears on top
      drawings: (container.drawings && Array.isArray(container.drawings) ? container.drawings : []).map(drawing => ({
        ...drawing,
        id: generateId('drawing')
      })),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Add container directly without creating ADD_CONTAINER history
    setContainers(prev => [...prev, duplicatedContainer]);
    
    // Push only DUPLICATE_CONTAINER command to history
    setTimeout(() => {
      pushCommand({
        type: COMMAND_TYPES.DUPLICATE_CONTAINER,
        sourceId: id,
        targetId: duplicatedContainer.id,
        sourceName: container.name,
        targetName: duplicatedContainer.name,
        offset: offset,
        drawingCount: (container.drawings && Array.isArray(container.drawings) ? container.drawings : []).length,
        newState: duplicatedContainer // Store full state for undo/redo
      });
    }, 0);
    
    // Auto-select the new container
    setSelectedContainerId(duplicatedContainer.id);
    
    return duplicatedContainer;
  }, [containers, pushCommand, setSelectedContainerId]);
  
  // Handle hover menu with timeout to prevent flickering
  
  const showHoverMenu = useCallback((containerId, x, y) => {
    // Clear any pending hide timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    setHoveredContainerId(containerId);
    setHoverMenuPosition({ x, y });
  }, []);
  
  const hideHoverMenu = useCallback(() => {
    // Add longer delay to prevent flickering when moving between container and menu
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredContainerId(null);
      hoverTimeoutRef.current = null;
    }, 300);
  }, []);
  
  // Direct container update without history (for undo/redo operations)
  const updateContainerDirect = useCallback((id, updates) => {
    setContainers(prev => {
      const containerIndex = prev.findIndex(c => c.id === id);
      if (containerIndex === -1) return prev;
      
      const updated = [...prev];
      updated[containerIndex] = {
        ...updated[containerIndex],
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      return updated;
    });
  }, []);

  // Add container directly without history (for undo/redo operations)
  const addContainerDirect = useCallback((containerData) => {
    // Ensure drawings is always an array
    const containerWithDrawings = {
      ...containerData,
      drawings: containerData.drawings || []
    };
    
    setContainers(prev => {
      const newContainers = [...prev, containerWithDrawings];
      return newContainers;
    });
    return containerWithDrawings;
  }, []);

  // Delete container directly without history (for undo/redo operations)
  const deleteContainerDirect = useCallback((id) => {
    setContainers(prev => prev.filter(c => c.id !== id));
    
    // Clear selection if deleted container was selected
    if (selectedContainerId === id) {
      setSelectedContainerId(null);
    }
    if (activeContainerForDrawing === id) {
      setActiveContainerForDrawing(null);
    }
  }, [selectedContainerId, activeContainerForDrawing]);

  // Clear all containers (for import functionality)
  const clearAllContainers = useCallback(() => {
    try {
      setContainers([]);
    } catch (error) {
      console.error('[ContainerContext] Error clearing containers:', error);
    }
  }, []);

  const value = {
    // State
    containers,
    selectedContainerId,
    activeContainerForDrawing,
    hoveredContainerId,
    hoverMenuPosition,
    
    // Container management
    addContainer,
    addContainerDirect,
    updateContainer,
    updateContainerDirect,
    deleteContainer,
    deleteContainerDirect,
    moveContainer,
    scaleContainer,
    rotateContainer,
    transformContainer,
    duplicateContainer,
    toggleLock,
    clearAllContainers,
    
    // Drawing management
    addDrawingToContainer,
    updateDrawingInContainer,
    deleteDrawingFromContainer,
    
    // Selection and interaction
    setSelectedContainerId,
    setActiveContainerForDrawing,
    showHoverMenu,
    hideHoverMenu,
    
    // Utility functions
    getContainersByLayer,
    getSelectedContainer,
    bringToFront,
    sendToBack
  };
  
  return (
    <ContainerContext.Provider value={value}>
      {children}
    </ContainerContext.Provider>
  );
};

export default ContainerContext; 